import {
  DataGridBlock,
  FeedbackBlock,
  FeedbackChoice,
  MessageBlockRole,
  SupportCtaChannel,
  TableBlock,
  WiseOwlActionType,
  WiseOwlBlock,
  WiseOwlBlockDisplayType,
  WiseOwlBlockType,
  WiseOwlEventName,
} from "../types";

const mockPromptSuggestionGroupBlock: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.PromptSuggestionGroup,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      categories: [
        { name: "SMS", suggestions: ["How do I ...?", "How do I ...?"] },
        { name: "Email", suggestions: ["How do I ...?", "How do I ...?"] },
      ],
    },
  },
];

const mockToolCallBlocks: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.ToolCall,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      name: "queryKnowledgeBase",
      input: "",
      output: "",
      formattedOutput: "",
    },
  },
  {
    type: WiseOwlBlockType.ToolCall,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      name: "getTwilioStatus",
      input: "",
      output: "",
      formattedOutput: "",
    },
  },
];

const mockMessageBlocks: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.Message,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      id: "9328739285",
      role: MessageBlockRole.User,
      message: "hello world!",
      isComplete: true,
      delta: null,
    },
  },
  {
    type: WiseOwlBlockType.Message,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      id: "79824789214789",
      role: MessageBlockRole.Assistant,
      message: `# Getting started with SMS notification
      
In this guide you will learn the fundamentals to build your first SMS notification app.

This guide covers:

- Getting a free trial number from Twilio
- Sending your first SMS
- A step-by-step tutorial on sending SMS notifications
- Best practices on scaling

### Getting a free trial number from Twilio

To send or receive SMS, you will need a phone number from Twilio. On your trial account, you can get one free USA or Canada phone number.

### Sending your first SMS

Next, copy and execute the following code in your command line/command prompt/terminal to send an SMS to yourself. You can expect to receive your SMS within 3 minutes. On a trial account, if you are sending an SMS to someone else, add their number to the verified caller ID first.

##### PC - Powershell (Older than Windows 10 version 1803)

Install Powershell if needed, and then open the Windows PowerShell.

\`\`\`bash
# Twilio account info and phone numbers.
$sid = "ACCOUNT_SID"
$token = "AUTH_TOKEN"
$phone_number = "PHONE_NUMBER"
$verified_number = "VERIFIED_NUMBER",

# Twilio API endpoint and POST params
$url = "https://api.twilio.com/2010-04-01/Accounts/$sid/Messages.json"
$params = @{ To = $verified_number ; From = $phone_number ; Body = "Hello from Twilio" }

# Create a credential object for HTTP basic auth
$p = $token | ConvertTo-SecureString -asPlainText -Force
$credential = New-Object System.Management.Automation.PSCredential($sid, $p)

# Make API request, selecting JSON properties from response
Invoke-WebRequest $url -Method Post -Credential $credential -Body $params -UseBasicParsing | ConvertFrom-Json | Select sid, body
\`\`\`
      `,
      isComplete: true,
      delta: null,
    },
  },
];

const mockLinkGroupBlock: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.LinkGroup,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      links: [
        {
          title: "article 1",
          url: "https://google.com",
          preview: "This article contains",
        },
        {
          title: "article 2 - My really super long article",
          url: "https://google.com",
          preview:
            "My really super long description, oh wow there is just so much content to see here!",
        },
        {
          title: "article 3",
          url: "https://google.com",
          preview: "This article contains",
        },
        {
          title: "article 4",
          url: "https://google.com",
          preview: "This article contains",
        },
        {
          title: "article 5",
          url: "https://google.com",
          preview: "This article contains",
        },
      ],
    },
  },
];

const mockButtonGroupBlock: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.ButtonGroup,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      buttons: [
        {
          text: "Submit a Ticket",
          action: {
            type: WiseOwlActionType.Redirect,
            values: {
              redirectUrl: "/submit",
            },
          },
        },
        {
          text: "Request a Callback",
          action: {
            type: WiseOwlActionType.Event,
            values: {
              name: WiseOwlEventName.SubmitTicket,
              metadata: {},
            },
          },
        },
        {
          text: "Live Chat",
          action: {
            type: WiseOwlActionType.Event,
            values: {
              name: WiseOwlEventName.SubmitTicket,
              metadata: {},
            },
          },
        },
      ],
    },
  },
];

const mockIntelligentDiscoveryHomeBlocks: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.IntelligentDiscoveryHome,
    display: WiseOwlBlockDisplayType.Persistent,
    values: {
      lastNDays: 7,
      failedMessages: 419,
      undeliveredMessages: 68,
      totalMessages: 1000,
    },
  },
];

const mockFeedbackBlock: FeedbackBlock = {
  type: WiseOwlBlockType.Feedback,
  display: WiseOwlBlockDisplayType.Persistent,
  values: {
    messageId: "1234",
    selected: null,
    supportCtaChannels: [SupportCtaChannel.Ticket],
  },
};

const mockFeedbackBlockWithSelection: FeedbackBlock = {
  type: WiseOwlBlockType.Feedback,
  display: WiseOwlBlockDisplayType.Persistent,
  values: {
    messageId: "1234",
    selected: FeedbackChoice.ThumbsUp,
    supportCtaChannels: [SupportCtaChannel.Ticket],
  },
};

const mockDataGridBlock = {
  type: "data-grid",
  display: "persistent",
  values: {
    description: `Resources: 
- MESSAGES
Date Range: 
- start: Fri, 14 Jun 2024 21:23:39 GMT
- end: Fri, 21 Jun 2024 21:23:39 GMT
Filters:
- status EQUALS [FAILED, UNDELIVERED, DELIVERY_UNKNOWN]
Aggregations:
- message_sid
- error_code`,
    rows: [
      {
        values: {
          sub_account_sid: "AC144809dc8faa2b76cc2e99c43a438323",
          status: "Undelivered",
          error_code: "30008",
          count: 28851,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC144809dc8faa2b76cc2e99c43a438323", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 1,
          sub_account_sid: 1,
        },
      },
      {
        values: {
          sub_account_sid: "AC37beed2e6cc8d7783abe87c3172c472c",
          status: "Failed",
          error_code: "12300",
          count: 13991,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC37beed2e6cc8d7783abe87c3172c472c", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 1,
          sub_account_sid: 3,
        },
      },
      {
        values: {
          sub_account_sid: "AC37beed2e6cc8d7783abe87c3172c472c",
          status: "Undelivered",
          error_code: "30008",
          count: 7041,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC37beed2e6cc8d7783abe87c3172c472c", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 2,
          sub_account_sid: 0,
        },
      },
      {
        values: {
          sub_account_sid: "AC37beed2e6cc8d7783abe87c3172c472c",
          status: "Undelivered",
          error_code: "30034",
          count: 11722,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC37beed2e6cc8d7783abe87c3172c472c", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 0,
          sub_account_sid: 0,
        },
      },
      {
        values: {
          sub_account_sid: "AC9e4370aad742bef0b4ca6869fa0e8eca",
          status: "Delivery Unknown",
          error_code: "0",
          count: 67856,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC9e4370aad742bef0b4ca6869fa0e8eca", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 1,
          sub_account_sid: 4,
        },
      },
      {
        values: {
          sub_account_sid: "AC9e4370aad742bef0b4ca6869fa0e8eca",
          status: "Failed",
          error_code: "0",
          count: 13599,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC9e4370aad742bef0b4ca6869fa0e8eca", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 2,
          sub_account_sid: 0,
        },
      },
      {
        values: {
          sub_account_sid: "AC9e4370aad742bef0b4ca6869fa0e8eca",
          status: "Failed",
          error_code: "30500",
          count: 126829,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC9e4370aad742bef0b4ca6869fa0e8eca", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 0,
          sub_account_sid: 0,
        },
      },
      {
        values: {
          sub_account_sid: "AC9e4370aad742bef0b4ca6869fa0e8eca",
          status: "Undelivered",
          error_code: "0",
          count: 13649,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "AC9e4370aad742bef0b4ca6869fa0e8eca", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 1,
          sub_account_sid: 0,
        },
      },
      {
        values: {
          sub_account_sid: "ACa145b9ae01d1c41b785a828e804895e5",
          status: "Failed",
          error_code: "63007",
          count: 9648,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "ACa145b9ae01d1c41b785a828e804895e5", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 1,
          sub_account_sid: 1,
        },
      },
      {
        values: {
          sub_account_sid: "ACb4c8821d2b224e4f51ee121892608151",
          status: "Undelivered",
          error_code: "30008",
          count: 15254,
        },
        actions: {
          sub_account_sid: {
            type: "user-say",
            values: {
              content:
                'Show me messages where "sub_account_sid" = "ACb4c8821d2b224e4f51ee121892608151", aggregated by status and error code.',
            },
          },
        },
        spans: {
          count: 1,
          error_code: 1,
          status: 1,
          sub_account_sid: 1,
        },
      },
    ],
    columns: [
      {
        name: "sub_account_sid",
        displayName: "Sub Account SID",
      },
      {
        name: "status",
        displayName: "Status",
      },
      {
        name: "error_code",
        displayName: "Error Code",
      },
      {
        name: "count",
        displayName: "Count",
      },
    ],
  },
} as DataGridBlock;

const mockTableBlock: TableBlock = {
  type: WiseOwlBlockType.TableBlock,
  values: {
    records: [
      {
        status: "FAILED",
        from_phone_number: "+16505766372",
        status_span: 1,
        from_phone_number_span: 1,
        total: 124172,
        error_code: "30500",
        error_code_span: 1,
      },
      {
        status: "UNDELIVERED",
        from_phone_number: "+14151235085",
        status_span: 1,
        from_phone_number_span: 1,
        total: 27239,
        error_code: "30008",
        error_code_span: 1,
      },
      {
        status: "DELIVERY_UNKNOWN",
        from_phone_number: "",
        status_span: 2,
        from_phone_number_span: 1,
        total: 21159,
        error_code: "0",
        error_code_span: 2,
      },
      {
        status: "DELIVERY_UNKNOWN",
        from_phone_number: "+16505766372",
        status_span: 0,
        from_phone_number_span: 1,
        total: 19507,
        error_code: "0",
        error_code_span: 0,
      },
      {
        status: "FAILED",
        from_phone_number: "+12015562933",
        status_span: 1,
        from_phone_number_span: 1,
        total: 16627,
        error_code: "12300",
        error_code_span: 1,
      },
      {
        status: "UNDELIVERED",
        from_phone_number: "+12015593193",
        status_span: 2,
        from_phone_number_span: 1,
        total: 15249,
        error_code: "30008",
        error_code_span: 1,
      },
      {
        status: "UNDELIVERED",
        from_phone_number: "+16505766372",
        status_span: 0,
        from_phone_number_span: 2,
        total: 14093,
        error_code: "0",
        error_code_span: 2,
      },
      {
        status: "FAILED",
        from_phone_number: "+16505766372",
        status_span: 2,
        from_phone_number_span: 0,
        total: 14051,
        error_code: "0",
        error_code_span: 0,
      },
      {
        status: "FAILED",
        from_phone_number: "whatsapp:+12092224703",
        status_span: 0,
        from_phone_number_span: 1,
        total: 9963,
        error_code: "63007",
        error_code_span: 1,
      },
      {
        status: "UNDELIVERED",
        from_phone_number: "+14155552345",
        status_span: 1,
        from_phone_number_span: 1,
        total: 6993,
        error_code: "30008",
        error_code_span: 1,
      },
    ],
  },
  display: WiseOwlBlockDisplayType.Persistent,
};

const mockAlternateTableBlock: TableBlock = {
  type: WiseOwlBlockType.TableBlock,
  display: WiseOwlBlockDisplayType.Persistent,
  values: {
    records: [
      {
        Total: 133980,
        "Error Code": "30500",
        "Error Code_span": 1,
      },
      {
        Total: 106025,
        "Error Code": "0",
        "Error Code_span": 1,
      },
      {
        Total: 66857,
        "Error Code": "30008",
        "Error Code_span": 1,
      },
      {
        Total: 15698,
        "Error Code": "12300",
        "Error Code_span": 1,
      },
      {
        Total: 13128,
        "Error Code": "30034",
        "Error Code_span": 1,
      },
      {
        Total: 10442,
        "Error Code": "63007",
        "Error Code_span": 1,
      },
      {
        Total: 7489,
        "Error Code": "30007",
        "Error Code_span": 1,
      },
      {
        Total: 6323,
        "Error Code": "21610",
        "Error Code_span": 1,
      },
      {
        Total: 2809,
        "Error Code": "30024",
        "Error Code_span": 1,
      },
      {
        Total: 2482,
        "Error Code": "63027",
        "Error Code_span": 1,
      },
    ],
  },
};

const mockTableBlockGroup: WiseOwlBlock[] = [
  {
    type: WiseOwlBlockType.Message,
    values: {
      isComplete: true,
      role: MessageBlockRole.Assistant,
      delta: null,
      message:
        "To identify the top countries with message delivery errors, please review the table below. It provides detailed insights into the status, error codes, and originating phone numbers for messages over the past 30 days.\n\nIf you need further assistance or specific recommendations based on this data, let me know!",
      id: "43aa0036-e671-43bb-b9ae-4b36facaf6f9",
    },
    display: WiseOwlBlockDisplayType.Persistent,
  },
  mockTableBlock,
];

const mockBlocks: WiseOwlBlock[] = [
  ...mockPromptSuggestionGroupBlock,
  ...mockToolCallBlocks,
  ...mockMessageBlocks,
  ...mockLinkGroupBlock,
  ...mockButtonGroupBlock,
  ...mockIntelligentDiscoveryHomeBlocks,
  ...mockTableBlockGroup,
];

export {
  mockPromptSuggestionGroupBlock,
  mockToolCallBlocks,
  mockMessageBlocks,
  mockLinkGroupBlock,
  mockButtonGroupBlock,
  mockBlocks,
  mockIntelligentDiscoveryHomeBlocks,
  mockTableBlock,
  mockAlternateTableBlock,
  mockTableBlockGroup,
  mockDataGridBlock,
  mockFeedbackBlock,
  mockFeedbackBlockWithSelection,
};
